import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Remove as RemoveIcon,
  TableChart as ExcelIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import Component from "components/Component";
import AutoCompleteTextField from "components/common/AutoCompleteTextField";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import DropdownButton from "components/common/DropdownButton";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import SICCodeSearch from "components/inputs/program/SICCodeSearch";
import StateOfFiling from "components/inputs/program/StateOfFiling";
import FeesTable from "components/inputs/sideCar/FeesTable";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as docGenService from "services/docGenService";
import * as exposureActions from "store/actions/input/exposure/exposureActions";
import * as programActions from "store/actions/input/program/programActions";
import * as towerPricingActions from "store/actions/pricing/tower/towerPricingActions";
import * as docGenHooks from "store/hooks/docGenHooks";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as towerPricingSelectors from "store/selectors/pricing/tower/towerPricingSelectors";
import * as staticDataSelectors from "store/selectors/temp/staticData/staticDataSelectors";
import * as XLSX from "xlsx";

const ReinsurerAutocomplete = ({ label, value, onChange, disabled }) => {
  const reinsurers = useSelector(
    (state) =>
      staticDataSelectors.selectConfig("reinsurers")(state)?.reinsurers ?? []
  );
  return (
    <Autocomplete
      options={reinsurers}
      getOptionKey={(option) => option.key}
      getOptionLabel={(option) => option.name}
      value={reinsurers.find((r) => r.key === value) ?? null}
      onChange={(_, newValue) => onChange(newValue?.key ?? null)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={"filled"}
          label={label}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
        />
      )}
      fullWidth
      disabled={disabled}
    />
  );
};

const TowerCarrierDropdown = ({ label, value, onChange }) => {
  const towerLayers = useSelector(towerPricingSelectors.getTower);
  const reinsurers = useSelector(
    (state) =>
      staticDataSelectors.selectConfig("reinsurers")(state)?.reinsurers ?? []
  );

  const options = useMemo(() => {
    const seen = new Set();
    const carriers = [];
    (towerLayers ?? []).forEach((layer) => {
      (layer?.shares ?? []).forEach((share) => {
        if (share?.carrier && !seen.has(share.carrier)) {
          seen.add(share.carrier);
          const name =
            reinsurers.find((r) => r.key === share.carrier)?.name ??
            share.carrier;
          carriers.push({ key: share.carrier, name });
        }
      });
    });
    return carriers;
  }, [towerLayers, reinsurers]);

  const selected = options.find((o) => o.key === value) ?? null;

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionKey={(option) =>
        typeof option === "string" ? option : option.key
      }
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.name
      }
      inputValue={selected?.name ?? value ?? ""}
      onInputChange={(_, newValue) => {
        const match = options.find((o) => o.name === newValue);
        const next = match?.key ?? (newValue || null);
        if (next !== (value ?? null)) onChange(next);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={"filled"}
          label={label}
          InputLabelProps={{ shrink: true }}
        />
      )}
      fullWidth
    />
  );
};

const buildFieldConfig = (currencySymbol) => [
  { key: "policyNumber", label: "Policy Number", type: "text" },
  { key: "insuredName", label: "Insured Name", type: "text" },
  { key: "insuredState", label: "Insured State", type: "state" },
  {
    key: "claimsMadeOrOccurrence",
    label: "Claims Made or Occurrence",
    type: "select",
    options: ["Occurrence", "Claims Made"],
  },
  {
    key: "rpsCarrierPartner",
    label: "Excess Path Carrier Partner",
    type: "carrier_partner",
  },
  {
    key: "underlyingCarrier",
    label: "Underlying Carrier",
    type: "tower_carrier",
  },
  { key: "sicCode", label: "SIC Code and Description", type: "sic" },
  {
    key: "primaryExposureValue",
    label: (primaryExposureName, primaryExposureUnits) => {
      const parts = [primaryExposureName ?? "Primary Exposure"];
      if (primaryExposureUnits) parts.push(primaryExposureUnits);
      return `Primary Exposure (${parts.join(", ")})`;
    },
    type: "number",
  },
  { key: "effectiveDate", label: "Effective Date", type: "date" },
  { key: "expirationDate", label: "Expiration Date", type: "date" },
  { key: "rpsExcessPathLimit", label: "RPS Excess Path Limit", type: "number" },
  { key: "grossLayerLimit", label: "Gross Layer Limit", type: "number" },
  { key: "attachmentPoint", label: "Attachment Point", type: "number" },
  { key: "insuredAddress", label: "Insured's Address", type: "text" },
];

const getLatestExposureValue = (data, exposureKey) => {
  if (exposureKey == null || data == null) {
    return null;
  }

  const yearsDesc = Object.keys(data)
    .map((year) => parseInt(year, 10))
    .filter((year) => Number.isFinite(year))
    .sort((a, b) => b - a);

  for (const year of yearsDesc) {
    const value = data?.[`${year}`]?.[exposureKey];
    if (value != null) {
      return value;
    }
  }

  return null;
};

const EXPOSURE_PRIORITY_KEYS = ["FLEET_SIZE", "REVENUE", "PAYROLL"];

const DEFAULT_SIDECAR_EXPOSURE2_KEY_CANDIDATES = ["TOTAL_VEHICLES"];

const SIDECAR_PROGRAM_KEYS = {
  claimsMadeOrOccurrence: "sidecarClaimsMadeOrOccurrence",
  rpsCarrierPartner: "sidecarCarrierPartner",
  underlyingCarrier: "sidecarUnderlyingCarrier",
  rpsExcessPathLimit: "sidecarRpsExcessPathLimit",
  grossLayerLimit: "sidecarGrossLayerLimit",
  attachmentPoint: "sidecarAttachmentPoint",
};

// Limit label for the Excel export: an explicit per-share limit type when
// set, otherwise derived from the claims-made/occurrence coverage trigger.
const getLimitTypeLabel = (share, isClaimsMade) =>
  share?.limitType || (isClaimsMade ? "Aggregate" : "Each Occurrence");

const LIMIT_TYPE_OPTIONS = ["Each Occurrence", "Aggregate"];

// Short display labels so the closed select stays single-height in the
// narrow schedule column; the stored value and export keep the full name.
const LIMIT_TYPE_DISPLAY_LABELS = {
  "Each Occurrence": "Each Occ.",
  Aggregate: "Aggregate",
};

const SideCar = () => {
  const dispatch = useDispatch();
  const program = useSelector(programSelectors.selectResolvedProgram);
  const currencySymbol = numbers.getCurrencySymbol(program?.currency);
  const exposureData = useSelector(exposureSelectors.getData);
  const primaryExposureKey = useSelector(exposureSelectors.getPrimaryExposure);
  const sidecarExposureList = useSelector(
    exposureSelectors.getExposureListExcludingRemoved
  );
  const exposureToggleList = useSelector(
    exposureSelectors.getExposureToggleList
  );
  const inceptionYear = useSelector(programSelectors.getInceptionYear);
  const primaryExposureItem =
    (sidecarExposureList ?? []).find((x) => x.key === primaryExposureKey) ??
    null;
  const primaryExposureName = primaryExposureItem?.name ?? null;
  const primaryExposureUnits = primaryExposureItem?.units ?? null;
  const fieldConfig = useMemo(
    () =>
      buildFieldConfig(currencySymbol).map((field) =>
        typeof field.label === "function"
          ? {
              ...field,
              label: field.label(primaryExposureName, primaryExposureUnits),
            }
          : field
      ),
    [currencySymbol, primaryExposureName, primaryExposureUnits]
  );
  const reinsurersConfig = useSelector(
    staticDataSelectors.selectConfig("reinsurers")
  );
  const configuredCoverageOptions = useSelector(
    (state) =>
      staticDataSelectors.selectConfig("submission_defaults")(state)
        ?.sidecarCoverageOptions ?? []
  );
  const towerLayers = useSelector(towerPricingSelectors.getTower);
  const companySic = program?.companySic ?? null;
  const secondTowerLayer = (towerLayers ?? [])[1] ?? null;

  const [pdfLoading, setPdfLoading] = useState(false);

  const { downloadTemplate } = docGenHooks.useDownloadTemplate({
    layerIndex: 0,
  });
  const { data: templates } = docGenService.useListTemplatesQuery();
  const scheduleTemplate = templates?.find(
    (t) => t?.title === "Schedule of Underlying"
  );

  const handleExportPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadTemplate({ template: scheduleTemplate });
    } finally {
      setPdfLoading(false);
    }
  };

  const [retro2ColumnVisible, setRetro2ColumnVisible] = useState(false);
  const hasAnyRetro2 = useMemo(
    () =>
      (towerLayers ?? []).some(
        (l) => (l?.shares ?? [])[0]?.retroactiveDate2 != null
      ),
    [towerLayers]
  );
  const showRetro2Column = retro2ColumnVisible || hasAnyRetro2;

  const coverageOptions = useMemo(() => {
    const fromTower = (towerLayers ?? [])
      .flatMap((l) => (l?.shares ?? []).map((s) => s?.coverage))
      .filter(Boolean);
    return [...new Set([...configuredCoverageOptions, ...fromTower])];
  }, [configuredCoverageOptions, towerLayers]);

  const latestExposureYear = useMemo(() => {
    if (exposureData == null) return null;
    const years = Object.keys(exposureData)
      .map((y) => parseInt(y, 10))
      .filter(Number.isFinite)
      .sort((a, b) => b - a);
    return years[0]?.toString() ?? null;
  }, [exposureData]);

  const sortedExposureOptions = useMemo(() => {
    const list = sidecarExposureList ?? [];
    const priority = EXPOSURE_PRIORITY_KEYS.map((k) =>
      list.find((e) => e.key === k)
    ).filter(Boolean);
    const rest = list
      .filter((e) => !EXPOSURE_PRIORITY_KEYS.includes(e.key))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...priority, ...rest];
  }, [sidecarExposureList]);

  const ensureExposureTrackedInHistoricalExposure = useCallback(
    (key) => {
      if (key == null || key === "") {
        return;
      }
      const toggleItem = (exposureToggleList ?? []).find((t) => t.key === key);
      if (toggleItem != null && !toggleItem.selected) {
        dispatch(
          exposureActions.toggleExposureMetric({
            key: toggleItem.key,
            selected: false,
            fromTemplate: toggleItem.fromTemplate,
          })
        );
      }
    },
    [dispatch, exposureToggleList]
  );

  const exposure2Key = program?.sidecarExposure2Key ?? null;
  const exposure2Item =
    (sidecarExposureList ?? []).find((x) => x.key === exposure2Key) ?? null;
  const exposure2Value =
    getLatestExposureValue(exposureData, exposure2Key) ?? null;

  const values = useMemo(
    () => ({
      policyNumber: program?.reference ?? "",
      insuredName: program?.insured ?? "",
      insuredState: program?.stateOfFiling ?? "",
      claimsMadeOrOccurrence: program?.sidecarClaimsMadeOrOccurrence ?? "",
      rpsCarrierPartner: program?.sidecarCarrierPartner ?? null,
      underlyingCarrier:
        program?.sidecarUnderlyingCarrier ??
        secondTowerLayer?.shares?.[0]?.carrier ??
        null,
      sicCode: companySic ?? null,
      primaryExposureValue: getLatestExposureValue(
        exposureData,
        primaryExposureKey
      ),
      effectiveDate: program?.inception ?? "",
      expirationDate: program?.expiration ?? "",
      rpsExcessPathLimit: program?.sidecarRpsExcessPathLimit ?? null,
      grossLayerLimit: program?.sidecarGrossLayerLimit ?? null,
      attachmentPoint: program?.sidecarAttachmentPoint ?? null,
      insuredAddress: program?.insuredAddress ?? "",
    }),
    [
      companySic,
      exposureData,
      primaryExposureKey,
      program?.inception,
      program?.expiration,
      program?.insured,
      program?.insuredAddress,
      program?.reference,
      program?.stateOfFiling,
      program?.sidecarCarrierPartner,
      program?.sidecarClaimsMadeOrOccurrence,
      program?.sidecarUnderlyingCarrier,
      program?.sidecarRpsExcessPathLimit,
      program?.sidecarGrossLayerLimit,
      program?.sidecarAttachmentPoint,
      secondTowerLayer,
    ]
  );

  useEffect(() => {
    const excessPathCarrierPartner = program?.sidecarCarrierPartner ?? null;
    const rpsExcessPathLimit = program?.sidecarRpsExcessPathLimit ?? null;
    const underlyingLayer =
      excessPathCarrierPartner != null
        ? (towerLayers ?? []).find((l) =>
            (l?.shares ?? []).some(
              (s) => s?.carrier === excessPathCarrierPartner
            )
          ) ?? null
        : null;

    const grossPremium = underlyingLayer?.grossPremium ?? null;
    const layerLimit = underlyingLayer?.limit ?? null;

    const computedPremium =
      grossPremium != null &&
      layerLimit != null &&
      layerLimit !== 0 &&
      rpsExcessPathLimit != null
        ? (grossPremium / layerLimit) * rpsExcessPathLimit + grossPremium
        : null;
    const computedLimit =
      layerLimit != null && rpsExcessPathLimit != null
        ? layerLimit + rpsExcessPathLimit
        : null;

    const currentPremium = program?.sidecarWithSidecarPremium ?? null;
    const currentLimit = program?.sidecarWithSidecarLimit ?? null;
    if (computedPremium !== currentPremium || computedLimit !== currentLimit) {
      dispatch(
        programActions.updateProgram({
          sidecarWithSidecarPremium: computedPremium,
          sidecarWithSidecarLimit: computedLimit,
        })
      );
    }
  }, [
    dispatch,
    program?.sidecarCarrierPartner,
    program?.sidecarRpsExcessPathLimit,
    program?.sidecarWithSidecarPremium,
    program?.sidecarWithSidecarLimit,
    towerLayers,
  ]);

  useEffect(() => {
    if (program?.sidecarExposure2Key != null) {
      return;
    }
    const list = sidecarExposureList ?? [];
    const defaultKey = DEFAULT_SIDECAR_EXPOSURE2_KEY_CANDIDATES.find((k) =>
      list.some((e) => e.key === k)
    );
    if (defaultKey == null) {
      return;
    }
    dispatch(
      programActions.updateProgram({
        sidecarExposure2Key: defaultKey,
      })
    );
  }, [dispatch, sidecarExposureList, program?.sidecarExposure2Key]);

  const updateValue = (key, value) => {
    switch (key) {
      case "insuredName":
        dispatch(programActions.updateInsured(value));
        break;
      case "policyNumber":
        dispatch(programActions.updateReference(value));
        break;
      case "insuredState":
        dispatch(programActions.updateStateOfFiling(value));
        break;
      case "effectiveDate":
        dispatch(programActions.updateInception(value));
        break;
      case "expirationDate":
        dispatch(programActions.updateExpiration(value));
        break;
      case "insuredAddress":
        dispatch(programActions.updateInsuredAddress(value));
        break;
      case "primaryExposureValue":
        if (primaryExposureKey != null && latestExposureYear != null) {
          dispatch(
            exposureActions.updateValue(
              latestExposureYear,
              primaryExposureKey,
              value
            )
          );
        }
        break;
      default:
        if (SIDECAR_PROGRAM_KEYS[key]) {
          dispatch(
            programActions.updateProgram({ [SIDECAR_PROGRAM_KEYS[key]]: value })
          );
        }
    }
  };

  const renderInput = (field) => {
    if (field.type === "number") {
      return (
        <PrettyNumberTextField
          InputLabelProps={{ shrink: true }}
          label={field.label}
          variant={"filled"}
          prefix={field.prefix}
          value={values[field.key]}
          onChangeNumber={(newValue) => updateValue(field.key, newValue)}
          fullWidth
          disabled={field.disabled}
        />
      );
    }

    if (field.type === "select") {
      return (
        <FormControl variant={"filled"} fullWidth disabled={field.disabled}>
          <InputLabel shrink={true}>{field.label}</InputLabel>
          <Select
            value={values[field.key] ?? ""}
            onChange={(event) => updateValue(field.key, event.target.value)}
            disabled={field.disabled}
          >
            {field.options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (field.type === "reinsurer") {
      return (
        <ReinsurerAutocomplete
          label={field.label}
          value={values[field.key]}
          onChange={(newValue) => updateValue(field.key, newValue)}
          disabled={field.disabled}
        />
      );
    }

    if (field.type === "carrier_partner") {
      return (
        <AutoCompleteTextField
          category={"input_sidecar_carrierPartner"}
          label={field.label}
          value={values[field.key] ?? ""}
          onChange={(e) => updateValue(field.key, e.target.value || null)}
          onChangeValue={(name) => updateValue(field.key, name || null)}
          variant={"filled"}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      );
    }

    if (field.type === "tower_carrier") {
      return (
        <TowerCarrierDropdown
          label={field.label}
          value={values[field.key]}
          onChange={(newValue) => updateValue(field.key, newValue)}
        />
      );
    }

    if (field.type === "sic") {
      return <SICCodeSearch />;
    }

    if (field.type === "date") {
      return (
        <FormattedDatePicker
          label={field.label}
          variant={"filled"}
          value={values[field.key] ?? ""}
          onChangeDate={(newValue) => updateValue(field.key, newValue)}
          fullWidth
          clearable={false}
        />
      );
    }

    if (field.type === "state") {
      return (
        <StateOfFiling
          label={field.label}
          value={values[field.key]}
          onChange={(newValue) => updateValue(field.key, newValue ?? "")}
        />
      );
    }

    return (
      <TextField
        label={field.label}
        value={values[field.key]}
        variant={"filled"}
        InputLabelProps={{ shrink: true }}
        onChange={(event) => updateValue(field.key, event.target.value)}
        fullWidth
      />
    );
  };

  const getField = (key) => fieldConfig.find((f) => f.key === key);

  const sectionHeader = (label) => (
    <Grid item xs={12} sx={{ mt: 1 }}>
      <Typography variant={"overline"} color={"text.secondary"}>
        {label}
      </Typography>
    </Grid>
  );

  const handleExport = () => {
    const str = (v) => (v == null ? "" : String(v));
    const num = (v) => (v == null ? null : Number(v));
    const bordereauHeaders = [
      "Policy Number",
      "Insured Name",
      "Insured State",
      "Claims Made or Occurrence",
      "RPS Excess Path Carrier Partner",
      "100% Gross Layer Premium",
      "100% Layer Limit",
      "RPS Excess Path Limit",
      "Excess Path Attachment Point",
      "Underlying Carrier",
      "SIC Code",
      "SIC Description",
      "Exposure 1",
      "Exposure 1 Value",
      "Exposure 2",
      "Exposure 2 Value",
    ];

    const underlyingLayer =
      values.rpsCarrierPartner != null
        ? (towerLayers ?? []).find((layer) =>
            (layer?.shares ?? []).some(
              (s) => s?.carrier === values.rpsCarrierPartner
            )
          ) ?? null
        : null;

    const proportionalPremium =
      underlyingLayer?.grossPremium != null &&
      underlyingLayer?.limit != null &&
      underlyingLayer.limit !== 0 &&
      values.rpsExcessPathLimit != null
        ? (underlyingLayer.grossPremium / underlyingLayer.limit) *
          values.rpsExcessPathLimit
        : null;

    const bordereauRow = [
      str(values.policyNumber),
      str(values.insuredName),
      str(values.insuredState),
      str(values.claimsMadeOrOccurrence),
      str(values.rpsCarrierPartner),
      num(
        proportionalPremium != null && underlyingLayer?.grossPremium != null
          ? proportionalPremium + underlyingLayer.grossPremium
          : underlyingLayer?.grossPremium
      ),
      num(
        underlyingLayer?.limit != null && values.rpsExcessPathLimit != null
          ? underlyingLayer.limit + values.rpsExcessPathLimit
          : underlyingLayer?.limit
      ),
      num(values.rpsExcessPathLimit),
      num(underlyingLayer?.attachment),
      str(values.underlyingCarrier),
      str(values.sicCode?.code ?? ""),
      str(values.sicCode?.title ?? ""),
      str(primaryExposureName ?? ""),
      num(values.primaryExposureValue),
      str(exposure2Item?.name ?? ""),
      num(exposure2Value),
    ];

    const policyRows = [
      [],
      ["POLICY ISSUANCE"],
      ["Named Insured:", str(values.insuredName)],
      [
        "Effective-Expiration Date:",
        `${str(values.effectiveDate)} - ${str(values.expirationDate)}`,
      ],
      ["XS Path Policy Number:", str(values.policyNumber)],
      ["Insured's Address:", str(values.insuredAddress)],
    ];

    const toExcelDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? str(dateStr) : dateStr;
    };

    // "Limits" header spans cols 5+6; col 5 = amount, col 6 = label
    const towerHeaders = [
      [],
      ["SCHEDULE OF UNDERLYING"],
      [
        "Line of Coverage",
        "Risk Company",
        "Effective Dates",
        "Expiry Date",
        "Premium",
        "Limits", // col 5 — merged with col 6 in header
        "", // col 6
        "Policy Number",
        "Retro Date",
        "Retro Date 2",
      ],
    ];

    const towerRows = [];
    const towerRowMerges = []; // { rowIndex } rows in towerRows that need col merges
    const limitCurrencySymbol = numbers.getCurrencySymbol(program?.currency);
    const isClaimsMade = values.claimsMadeOrOccurrence === "Claims Made";
    const fmtLimitStr = (v) => {
      const abbr = numbers.abbreviated(v);
      return abbr == null ? "" : `${limitCurrencySymbol}${abbr}`;
    };

    (towerLayers ?? []).forEach((layer) => {
      const shares = layer?.shares ?? [];
      const firstShare = shares[0] ?? null;
      const limit = layer?.limit;
      const aggregateLimit = firstShare?.shareOfAggregateLimit;

      const mainLimitLabel = getLimitTypeLabel(firstShare, isClaimsMade);
      const shareRows =
        shares.length > 0
          ? shares
          : [
              {
                carrier: null,
                reference: "",
                retroactiveDate: "",
                retroactiveDate2: "",
              },
            ];

      const mainRowIndex = towerRows.length;
      shareRows.forEach((share, shareIdx) => {
        const carrierName =
          (reinsurersConfig?.reinsurers ?? []).find(
            (r) => r.key === share?.carrier
          )?.name ??
          share?.carrier ??
          "";
        const policyNumber = share?.reference ?? "";
        const retroDate = share?.retroactiveDate ?? "";
        const retroDate2 = share?.retroactiveDate2 ?? "";
        towerRows.push([
          share?.coverage ?? "",
          carrierName,
          shareIdx === 0 ? toExcelDate(values.effectiveDate) : "",
          shareIdx === 0 ? toExcelDate(values.expirationDate) : "",
          shareIdx === 0 ? num(layer?.grossPremium) : "",
          shareIdx === 0 ? fmtLimitStr(limit) : "",
          shareIdx === 0 ? mainLimitLabel : "",
          policyNumber,
          toExcelDate(retroDate),
          toExcelDate(retroDate2),
        ]);
      });
      // Emit the aggregate sub-row only when the main line is not already
      // labelled Aggregate (explicit limitType or claims-made derivation),
      // so the pair always reads as one occurrence line plus one aggregate.
      if (aggregateLimit != null && mainLimitLabel !== "Aggregate") {
        towerRows.push([
          "",
          "",
          "",
          "",
          "",
          fmtLimitStr(aggregateLimit),
          "Aggregate",
          "",
          "",
          "",
        ]);
        towerRowMerges.push(mainRowIndex);
      }
    });

    const wsData = [
      ["BORDEREAU INFO"],
      bordereauHeaders,
      bordereauRow,
      ...policyRows,
      ...towerHeaders,
      ...towerRows,
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Merge "Limits" header across amount col (5) and label col (6)
    const limitsHeaderRow = 1 + 1 + 1 + policyRows.length + 2;
    const merges = [
      { s: { r: limitsHeaderRow, c: 5 }, e: { r: limitsHeaderRow, c: 6 } },
    ];

    // For layers with aggregate sub-row, merge all other columns across both rows
    if (towerRowMerges.length > 0) {
      const towerDataStart = limitsHeaderRow + 1;
      const mergeCols = [0, 1, 2, 3, 4, 7, 8, 9]; // all except 5 (amount) and 6 (label)
      towerRowMerges.forEach((rowIndex) => {
        const r = towerDataStart + rowIndex;
        mergeCols.forEach((c) => {
          merges.push({ s: { r, c }, e: { r: r + 1, c } });
        });
      });
    }

    ws["!merges"] = merges;

    // Apply thousand-separator format to every numeric cell
    Object.keys(ws)
      .filter((key) => !key.startsWith("!"))
      .forEach((addr) => {
        if (ws[addr].t === "n") ws[addr].z = "#,##0";
      });

    const maxCols = Math.max(bordereauHeaders.length, 10);
    ws["!cols"] = Array.from({ length: maxCols }, (_, i) =>
      i === 1 || i === 4 || i === 9 || i === 10 ? { wch: 35 } : { wch: 22 }
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bordereau");

    const filename = `${values.insuredName || "Bordereau"} - Bordereau.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <Component
      title={"Bordereau Information"}
      options={
        <DropdownButton
          label={"Export"}
          isLoading={pdfLoading}
          options={[
            {
              label: "Excel",
              icon: <ExcelIcon fontSize={"small"} />,
              onClick: handleExport,
            },
            {
              label: "PDF",
              icon: <PdfIcon fontSize={"small"} />,
              onClick: handleExportPdf,
              disabled: !scheduleTemplate,
            },
          ]}
        />
      }
    >
      <Grid container spacing={2}>
        {sectionHeader("Insured")}
        <Grid item xs={6}>
          {renderInput(getField("policyNumber"))}
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("insuredName"))}
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("insuredState"))}
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("claimsMadeOrOccurrence"))}
        </Grid>

        {sectionHeader("Carriers")}
        <Grid item xs={6}>
          {renderInput(getField("rpsCarrierPartner"))}
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("underlyingCarrier"))}
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("rpsExcessPathLimit"))}
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("grossLayerLimit"))}
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("attachmentPoint"))}
        </Grid>
        {sectionHeader("Schedule of Underlying")}
        <Grid item xs={12}>
          <Stack direction={"column"} spacing={2}>
            <TableContainer>
              <Table size={"small"}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 155 }}>
                      {"Line of Coverage"}
                    </TableCell>
                    <TableCell>{"Risk Company"}</TableCell>
                    <TableCell sx={{ width: 110 }}>
                      {"Effective Dates"}
                    </TableCell>
                    <TableCell sx={{ width: 120 }} align={"right"}>
                      {"Premium"}
                    </TableCell>
                    <TableCell sx={{ width: 130 }} align={"right"}>
                      {"Limits"}
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>{"Limit Type"}</TableCell>
                    <TableCell sx={{ width: 130 }} align={"right"}>
                      {"Aggregate Limit"}
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>{"Policy Number"}</TableCell>
                    <TableCell sx={{ width: 130 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {"Retro Date"}
                        {!showRetro2Column && (
                          <Tooltip title={"Add additional retro date"}>
                            <IconButton
                              size={"small"}
                              onClick={() => setRetro2ColumnVisible(true)}
                            >
                              <AddIcon fontSize={"inherit"} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {showRetro2Column && (
                      <TableCell sx={{ width: 130 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {"Retro Date (2)"}
                          <Tooltip title={"Remove retro date (2)"}>
                            <IconButton
                              size={"small"}
                              onClick={() => setRetro2ColumnVisible(false)}
                            >
                              <RemoveIcon fontSize={"inherit"} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(towerLayers ?? []).map((layer, index) => {
                    const layerId = layer?.id ?? index;
                    const shares = layer?.shares ?? [];
                    const isSidecarLayer =
                      values.rpsCarrierPartner != null &&
                      (layer?.shares ?? []).some(
                        (s) => s?.carrier === values.rpsCarrierPartner
                      );
                    const sidecarPropPremium =
                      isSidecarLayer &&
                      layer?.grossPremium != null &&
                      layer?.limit != null &&
                      layer.limit !== 0 &&
                      values.rpsExcessPathLimit != null
                        ? (layer.grossPremium / layer.limit) *
                          values.rpsExcessPathLimit
                        : null;
                    const sidecarPremium =
                      sidecarPropPremium != null
                        ? sidecarPropPremium + layer.grossPremium
                        : null;
                    const sidecarLimit =
                      isSidecarLayer &&
                      layer?.limit != null &&
                      values.rpsExcessPathLimit != null
                        ? layer.limit + values.rpsExcessPathLimit
                        : null;
                    const fmt = (v) =>
                      v == null
                        ? "—"
                        : v.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          });
                    const updateShare = (shareIndex, shareValues) =>
                      dispatch(
                        towerPricingActions.updateShare({
                          layerIndex: index,
                          shareIndex,
                          values: shareValues,
                        })
                      );
                    const datePickerSlotProps = {
                      textField: {
                        variant: "filled",
                        size: "small",
                        hiddenLabel: true,
                      },
                    };
                    const cellSx = { px: 0.5, py: 0.75 };
                    const noBorderSx = {
                      ...cellSx,
                      borderBottom: "none",
                    };
                    const sidecarCellSx = {
                      ...cellSx,
                      color: "primary.main",
                      fontWeight: 600,
                      pb: 0.5,
                    };
                    const shareRows = shares.length > 0 ? shares : [null];

                    return (
                      <Fragment key={layerId}>
                        {shareRows.map((share, shareIdx) => {
                          const rowKey = `${layerId}-share-${shareIdx}`;

                          // Only show/allow editing of layer-level fields on first share row.
                          const showLayerFields = shareIdx === 0;
                          const rowCellSx = isSidecarLayer
                            ? noBorderSx
                            : cellSx;
                          const rowCellSxPx1 = isSidecarLayer
                            ? { ...noBorderSx, px: 1 }
                            : { ...cellSx, px: 1 };

                          return (
                            <TableRow
                              key={rowKey}
                              sx={{
                                "& .sidecar-share-action": {
                                  opacity: 0,
                                  transition: "opacity 120ms ease-in-out",
                                },
                                "&:hover .sidecar-share-action": {
                                  opacity: 1,
                                },
                              }}
                            >
                              <TableCell sx={rowCellSx}>
                                <Autocomplete
                                  freeSolo
                                  size={"small"}
                                  options={coverageOptions}
                                  value={share?.coverage ?? ""}
                                  onChange={(_, v) =>
                                    updateShare(shareIdx, { coverage: v ?? "" })
                                  }
                                  onInputChange={(_, v) =>
                                    updateShare(shareIdx, { coverage: v ?? "" })
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant={"filled"}
                                      hiddenLabel
                                      size={"small"}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={rowCellSxPx1}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <TextField
                                    variant={"filled"}
                                    size={"small"}
                                    hiddenLabel
                                    fullWidth
                                    value={share?.carrier ?? ""}
                                    onChange={(e) =>
                                      updateShare(shareIdx, {
                                        carrier: e.target.value,
                                      })
                                    }
                                    disabled={share == null}
                                  />
                                  {shareIdx === 0 && (
                                    <Disabled ifReadOnly>
                                      <Tooltip title={"Add Share"}>
                                        <span>
                                          <IconButton
                                            className={"sidecar-share-action"}
                                            size={"small"}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              updateShare(shares.length, {});
                                            }}
                                            disabled={share == null}
                                          >
                                            <AddIcon fontSize={"inherit"} />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Disabled>
                                  )}
                                  {shareIdx > 0 && (
                                    <Disabled ifReadOnly>
                                      <Tooltip title={"Remove Share"}>
                                        <span>
                                          <IconButton
                                            className={"sidecar-share-action"}
                                            size={"small"}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              dispatch(
                                                towerPricingActions.deleteShare(
                                                  {
                                                    layerIndex: index,
                                                    shareIndex: shareIdx,
                                                  }
                                                )
                                              );
                                            }}
                                            disabled={share == null}
                                          >
                                            <DeleteIcon fontSize={"inherit"} />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </Disabled>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={rowCellSxPx1}>
                                {showLayerFields ? (
                                  <Typography variant={"body2"} noWrap>
                                    {values.effectiveDate || "—"}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell sx={rowCellSx} align={"right"}>
                                {share?.coverage === "Sidecar" ? (
                                  <Typography variant={"body2"} pr={"0.75rem"}>
                                    {fmt(sidecarPropPremium)}
                                  </Typography>
                                ) : showLayerFields ? (
                                  <PrettyNumberTextField
                                    variant={"filled"}
                                    size={"small"}
                                    hiddenLabel
                                    fullWidth
                                    inputProps={{
                                      style: { textAlign: "right" },
                                    }}
                                    value={layer?.grossPremium ?? null}
                                    onChangeNumber={(v) =>
                                      dispatch(
                                        towerPricingActions.updateMarketGrossWrittenPremium(
                                          index,
                                          v
                                        )
                                      )
                                    }
                                  />
                                ) : null}
                              </TableCell>
                              <TableCell sx={rowCellSx} align={"right"}>
                                {share?.coverage === "Sidecar" ? (
                                  <Typography variant={"body2"} pr={"0.75rem"}>
                                    {fmt(values.rpsExcessPathLimit)}
                                  </Typography>
                                ) : showLayerFields ? (
                                  <PrettyNumberTextField
                                    variant={"filled"}
                                    size={"small"}
                                    hiddenLabel
                                    fullWidth
                                    inputProps={{
                                      style: { textAlign: "right" },
                                    }}
                                    value={layer?.limit ?? null}
                                    onChangeNumber={(v) =>
                                      dispatch(
                                        towerPricingActions.updateLimit(
                                          index,
                                          v
                                        )
                                      )
                                    }
                                  />
                                ) : null}
                              </TableCell>
                              <TableCell sx={rowCellSx}>
                                {share?.coverage === "Sidecar" ? null : (
                                  <TextField
                                    select
                                    variant={"filled"}
                                    size={"small"}
                                    hiddenLabel
                                    fullWidth
                                    value={share?.limitType ?? ""}
                                    onChange={(e) =>
                                      updateShare(shareIdx, {
                                        limitType: e.target.value || null,
                                      })
                                    }
                                    SelectProps={{
                                      renderValue: (v) =>
                                        LIMIT_TYPE_DISPLAY_LABELS[v] ?? v,
                                    }}
                                  >
                                    <MenuItem value={""}>{"—"}</MenuItem>
                                    {LIMIT_TYPE_OPTIONS.map((option) => (
                                      <MenuItem key={option} value={option}>
                                        {option}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                )}
                              </TableCell>
                              <TableCell sx={rowCellSx} align={"right"}>
                                {share?.coverage === "Sidecar" ? null : (
                                  <PrettyNumberTextField
                                    variant={"filled"}
                                    size={"small"}
                                    hiddenLabel
                                    fullWidth
                                    inputProps={{
                                      style: { textAlign: "right" },
                                    }}
                                    value={share?.shareOfAggregateLimit ?? null}
                                    onChangeNumber={(v) =>
                                      updateShare(shareIdx, {
                                        shareOfAggregateLimit: v,
                                      })
                                    }
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={rowCellSx}>
                                <TextField
                                  variant={"filled"}
                                  size={"small"}
                                  hiddenLabel
                                  fullWidth
                                  value={share?.reference ?? ""}
                                  onChange={(e) =>
                                    updateShare(shareIdx, {
                                      reference: e.target.value,
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell sx={rowCellSx}>
                                <FormattedDatePicker
                                  value={share?.retroactiveDate ?? null}
                                  onChangeDate={(v) =>
                                    updateShare(shareIdx, {
                                      retroactiveDate: v,
                                    })
                                  }
                                  slotProps={datePickerSlotProps}
                                />
                              </TableCell>
                              {showRetro2Column && (
                                <TableCell sx={rowCellSx}>
                                  <FormattedDatePicker
                                    value={share?.retroactiveDate2 ?? null}
                                    onChangeDate={(v) =>
                                      updateShare(shareIdx, {
                                        retroactiveDate2: v,
                                      })
                                    }
                                    slotProps={datePickerSlotProps}
                                  />
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                        {isSidecarLayer && (
                          <TableRow key={`${layerId}-sidecar`}>
                            <TableCell sx={{ py: 0 }} />
                            <TableCell
                              sx={{
                                ...sidecarCellSx,
                                px: 1,
                                fontSize: "0.75rem",
                              }}
                            >
                              {"↳ With Sidecar"}
                            </TableCell>
                            <TableCell sx={{ py: 0 }} />
                            <TableCell
                              sx={{ ...sidecarCellSx, pr: "1rem" }}
                              align={"right"}
                            >
                              {fmt(sidecarPremium)}
                            </TableCell>
                            <TableCell
                              sx={{ ...sidecarCellSx, pr: "1rem" }}
                              align={"right"}
                            >
                              {fmt(sidecarLimit)}
                            </TableCell>
                            <TableCell
                              colSpan={showRetro2Column ? 5 : 4}
                              sx={{ py: 0 }}
                            />
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction={"row"}>
              <Disabled ifReadOnly>
                <Button
                  onClick={() => {
                    const newLayerIndex = towerLayers?.length ?? 0;
                    dispatch(towerPricingActions.addLayer(newLayerIndex));
                    // Most sidecar policies have limit = aggregate, so layers
                    // added from this page default to an Aggregate limit type.
                    dispatch(
                      towerPricingActions.updateShare({
                        layerIndex: newLayerIndex,
                        shareIndex: 0,
                        values: { limitType: "Aggregate" },
                      })
                    );
                  }}
                >
                  {"Add Layer"}
                </Button>
              </Disabled>
            </Stack>
          </Stack>
        </Grid>

        {sectionHeader("Fees & Commissions")}
        <Grid item xs={12}>
          <FeesTable
            layerPremium={program?.sidecarWithSidecarPremium ?? null}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant={"caption"}
            color={"text.secondary"}
            display={"block"}
            sx={{ mb: 0.5 }}
          >
            {"TRIA Charge"}
          </Typography>
          <PrettyNumberTextField
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
            inputProps={{ style: { textAlign: "right" } }}
            value={program?.sidecarTriaCharge ?? null}
            onChangeNumber={(v) =>
              dispatch(programActions.updateProgram({ sidecarTriaCharge: v }))
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant={"caption"}
            color={"text.secondary"}
            display={"block"}
            sx={{ mb: 0.5 }}
          >
            {"TRIA Surplus Lines Tax"}
          </Typography>
          <PrettyNumberTextField
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
            inputProps={{ style: { textAlign: "right" } }}
            value={program?.sidecarTriaSurplusLinesTax ?? null}
            onChangeNumber={(v) =>
              dispatch(
                programActions.updateProgram({ sidecarTriaSurplusLinesTax: v })
              )
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant={"caption"}
            color={"text.secondary"}
            display={"block"}
            sx={{ mb: 0.5 }}
          >
            {"Agent Commission"}
          </Typography>
          <PrettyNumberTextField
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
            suffix={"%"}
            inputProps={{ style: { textAlign: "right" } }}
            disabled={program?.sidecarCommissionIsNet ?? false}
            value={
              program?.sidecarCommissionIsNet
                ? 0
                : program?.sidecarAgentCommission ?? null
            }
            onChangeNumber={(v) =>
              dispatch(
                programActions.updateProgram({ sidecarAgentCommission: v })
              )
            }
          />
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant={"caption"}
            color={"text.secondary"}
            display={"block"}
            sx={{ mb: 0.5 }}
          >
            {"Gross Commission"}
          </Typography>
          <PrettyNumberTextField
            variant={"filled"}
            size={"small"}
            hiddenLabel
            fullWidth
            suffix={"%"}
            inputProps={{ style: { textAlign: "right" } }}
            value={program?.sidecarGrossCommission ?? null}
            onChangeNumber={(v) =>
              dispatch(
                programActions.updateProgram({ sidecarGrossCommission: v })
              )
            }
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={
              <Checkbox
                size={"small"}
                checked={program?.sidecarCommissionIsNet ?? false}
                onChange={(e) => {
                  const isNet = e.target.checked;
                  dispatch(
                    programActions.updateProgram({
                      sidecarCommissionIsNet: isNet,
                      ...(isNet ? { sidecarAgentCommission: 0 } : {}),
                    })
                  );
                }}
              />
            }
            label={"Net"}
          />
        </Grid>
        <Grid item xs={6} />

        {sectionHeader("Policy Issuance")}
        <Grid item xs={6}>
          {renderInput(getField("effectiveDate"))}
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("expirationDate"))}
        </Grid>
        <Grid item xs={12}>
          {renderInput(getField("insuredAddress"))}
        </Grid>

        {sectionHeader("Exposure & Classification")}
        <Grid item xs={6}>
          <FormControl variant={"filled"} fullWidth>
            <InputLabel shrink={true}>{"Exposure 1"}</InputLabel>
            <Select
              value={primaryExposureKey ?? ""}
              onChange={(e) => {
                const newKey = e.target.value || null;
                ensureExposureTrackedInHistoricalExposure(newKey);
                dispatch(exposureActions.updatePrimaryExposure(newKey));
              }}
            >
              <MenuItem value={""}>{"—"}</MenuItem>
              {sortedExposureOptions.map((exp) => (
                <MenuItem key={exp.key} value={exp.key}>
                  {exp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <PrettyNumberTextField
            variant={"filled"}
            label={
              primaryExposureItem
                ? `${primaryExposureItem.name} (${primaryExposureItem.units})`
                : "Exposure 1 Value"
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={values.primaryExposureValue}
            onChangeNumber={(v) =>
              primaryExposureKey != null &&
              dispatch(
                exposureActions.updateValue(
                  latestExposureYear ?? inceptionYear,
                  primaryExposureKey,
                  v
                )
              )
            }
            disabled={
              primaryExposureKey == null ||
              (latestExposureYear == null && inceptionYear == null)
            }
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl variant={"filled"} fullWidth>
            <InputLabel shrink={true}>{"Exposure 2"}</InputLabel>
            <Select
              value={exposure2Key ?? ""}
              onChange={(e) => {
                const newKey = e.target.value || null;
                ensureExposureTrackedInHistoricalExposure(newKey);
                dispatch(
                  programActions.updateProgram({
                    sidecarExposure2Key: newKey,
                  })
                );
              }}
            >
              <MenuItem value={""}>{"—"}</MenuItem>
              {sortedExposureOptions.map((exp) => (
                <MenuItem key={exp.key} value={exp.key}>
                  {exp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <PrettyNumberTextField
            variant={"filled"}
            label={
              exposure2Item
                ? `${exposure2Item.name} (${exposure2Item.units})`
                : "Exposure 2 Value"
            }
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={exposure2Value}
            onChangeNumber={(v) =>
              exposure2Key != null &&
              dispatch(
                exposureActions.updateValue(
                  latestExposureYear ?? inceptionYear,
                  exposure2Key,
                  v
                )
              )
            }
            disabled={
              exposure2Key == null ||
              (latestExposureYear == null && inceptionYear == null)
            }
          />
        </Grid>
        <Grid item xs={6}>
          {renderInput(getField("sicCode"))}
        </Grid>
      </Grid>
    </Component>
  );
};

export default SideCar;
